import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOne, useUpdate, useList } from "@refinedev/core";
import { VaultSidebar, VaultDocItem } from "@/components/digibox/VaultSidebar";
import { VaultArticle } from "@/components/digibox/VaultArticle";
import { VaultActionBar } from "@/components/digibox/VaultActionBar";

function mapAcceptanceStatus(status?: string): VaultDocItem["status"] {
  if (!status || status === "Pending") return "action";
  if (status === "Final Acceptance" || status === "Accepted") return "accepted";
  if (status === "Read") return "read";
  return "pending";
}

export const AcceptanceAction: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const { data, isLoading } = useOne({
    resource: "acceptance",
    id: id!,
  });

  const { data: allTasksData, isLoading: tasksLoading } = useList({
    resource: "acceptance",
    pagination: { pageSize: 50 },
    sorters: [{ field: "acclastdate", order: "desc" }],
  });

  const { mutateAsync: updateAcceptance } = useUpdate();

  const acceptance = data?.data as any;
  const allTasks = (allTasksData?.data || []) as any[];

  const isAlreadyDone =
    acceptance?.acceptance_status === "Final Acceptance" ||
    acceptance?.acceptance_status === "Accepted" ||
    acceptance?.acceptance_status === "Read";

  const documents: VaultDocItem[] = allTasks.map((t: any) => ({
    id: t.id,
    category: t.accmethod || "Task",
    title: t.acctitle || "Untitled Task",
    date: t.acclastdate
      ? new Date(t.acclastdate).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        })
      : "—",
    status: mapAcceptanceStatus(t.acceptance_status),
  }));

  const handleSelect = (docId: string) => {
    if (docId !== id) {
      setCompleted(false);
      navigate(`/digibox/acceptance/${docId}`);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await updateAcceptance({
        resource: "acceptance",
        id: id!,
        values: {
          acceptance_status: "Final Acceptance",
          acclastdate: new Date().toISOString().slice(0, 10),
        },
      });
      setCompleted(true);
    } catch {
      // Error handled by refine notification provider
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <VaultSidebar
        documents={documents}
        activeId={id}
        onSelect={handleSelect}
        loading={tasksLoading}
      />

      <main className="vault-panel vault-main-content">
        {isLoading ? (
          <div className="vault-loading">Loading...</div>
        ) : !acceptance ? (
          <div className="vault-empty">Task not found</div>
        ) : (
          <>
            <VaultArticle
              title={acceptance.acctitle || "Acceptance Task"}
              documentId={acceptance.evacceptance_no || `ACC-${id}`}
              sidebarContent={
                acceptance.description
                  ? `<p>${acceptance.description}</p>`
                  : undefined
              }
              mainContent={acceptance.description}
            />

            {completed || isAlreadyDone ? (
              <VaultActionBar
                statement=""
                buttonLabel=""
                onAction={async () => {}}
                completed
                completedLabel={
                  isAlreadyDone
                    ? `${acceptance.acceptance_status} — ${acceptance.acclastdate || "—"}`
                    : "Completed"
                }
              />
            ) : (
              <VaultActionBar
                statement="I acknowledge receipt and confirm that I have reviewed this filing. By accepting, I confirm my agreement with the terms outlined."
                buttonLabel="Accept & Confirm"
                requiresConfirmation
                confirmWord="accetto"
                onAction={handleAccept}
                loading={actionLoading}
              />
            )}
          </>
        )}
      </main>
    </>
  );
};
